import type { AuthMethod } from "@/lib/api";
import { humanizeError } from "@/lib/errors";

// Shared building blocks for the connect-an-integration flow. Extracted from the
// old monolithic ConnectDialog so the wizard page and the reconnect/edit dialog
// can share one source of truth (state + validation + submit live in
// useConnectForm; the field UI lives in sections.tsx).

// Native <select> styling matched to the Input component (no Select primitive
// exists in this design system).
export const SELECT_CLS =
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:border-ring";

// Shared textarea styling (matches Input focus ring).
export const TEXTAREA_CLS =
  "flex w-full rounded-md border border-input bg-transparent px-3 py-2 font-mono text-xs shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:border-ring";

export const SERVER_NAME_RE = /^[A-Za-z0-9_-]{1,64}$/;
export const SERVER_NAME_HINT =
  "Use only letters, numbers, hyphens, and underscores (max 64 characters).";
export const URL_RE = /^https?:\/\/.+/i;

// Delegate to the shared humanizer so raw backend codes/status text never reach
// the user.
export function errMsg(e: unknown, fallback: string): string {
  return humanizeError(e, fallback);
}

export function hostOfURL(u: string): string {
  try {
    return new URL(u).host;
  } catch {
    return u;
  }
}

export const METHOD_LABELS: Record<AuthMethod, string> = {
  none: "No authentication",
  bearer: "Bearer token",
  api_key: "API key (custom header)",
  basic: "Username & password",
  headers: "Custom headers",
  oauth2_client_credentials: "OAuth — client credentials (machine)",
  oauth2_authorization_code: "OAuth — sign in (browser)",
};

// A short, friendly one-liner per method for the picker, so a non-technical user
// can tell them apart without prior knowledge.
export const METHOD_BLURBS: Record<AuthMethod, string> = {
  none: "The service is public — no credentials needed.",
  bearer: "A single secret token sent on every request.",
  api_key: "A key sent in a header you name (e.g. X-API-Key).",
  basic: "A username and password.",
  headers: "One or more custom header name/value pairs.",
  oauth2_client_credentials: "Machine-to-machine OAuth with a client ID & secret.",
  oauth2_authorization_code: "Sign in through the provider in your browser.",
};

export type Header = { key: string; value: string };

export type HTTPMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
export const HTTP_METHODS: HTTPMethod[] = ["GET", "POST", "PUT", "PATCH", "DELETE"];
export const METHODS_WITH_BODY: HTTPMethod[] = ["POST", "PUT", "PATCH"];

// shQuote wraps a value in single quotes for a POSIX shell, escaping any embedded
// single quote as the standard '\'' sequence (close-quote, escaped literal quote,
// reopen-quote).
const shQuote = (v: string) => `'${v.replace(/'/g, "'\\''")}'`;

// buildCurlFromForm compiles the form-builder fields into a single cURL command
// the backend parses into one MCP tool. Auth is intentionally omitted — the
// sealed auth envelope carries credentials, and the backend strips credential
// headers from a cURL anyway.
export function buildCurlFromForm(method: HTTPMethod, url: string, headers: Header[], body: string): string {
  const parts = ["curl", "-X", method, shQuote(url.trim())];
  for (const h of headers) {
    const key = h.key.trim();
    if (!key) continue;
    parts.push("-H", shQuote(`${key}: ${h.value}`));
  }
  if (METHODS_WITH_BODY.includes(method) && body.trim()) {
    parts.push("--data", shQuote(body));
  }
  return parts.join(" ");
}
